import { ApiError, type ConflictCode, type ForbiddenCode, type UnavailableCode } from '@pc/api/errors';
import { loginUrl } from '../auth/useMe';
import { OpId } from './ui';

/**
 * The single place failures are turned into words.
 *
 * Centralised because the distinctions the API preserves are easy to lose one
 * component at a time — especially 403 vs 503, where collapsing them would tell
 * every operator their access was revoked during an Okta outage.
 *
 * Two things are being said at once here, and their order is the whole design.
 * The API often knows exactly what went wrong and what to do about it ("VMID
 * 129 is locked. Unlock the LXC before starting it."); this file only knows
 * what the status code means in general. When both exist the specific one
 * leads and the general one follows as context, because a generic preamble
 * placed above real server text can flatly contradict it — a 502 used to open
 * with "This is not something you did wrong" directly above an instruction
 * telling the operator what to do about it. Generic copy is never allowed to
 * assert a cause, or a fault, over a message that says otherwise.
 */

const FORBIDDEN_COPY: Record<ForbiddenCode, { title: string; detail: string }> = {
  // Three genuinely different problems with three different fixes. Do not
  // merge these into one "access denied" string.
  'not-an-operator': {
    title: 'You are not a platform operator',
    detail:
      'Your account is not in platformctl-operators or any platformctl-l1…l5 group. ' +
      'Ask IT to grant operator access.',
  },
  'level-too-low': {
    title: 'Your access level is too low for this action',
    detail: 'The action is real and available — your level does not reach it. Ask for an escalation.',
  },
  'level-unreadable': {
    title: 'Your access level could not be read',
    detail:
      'Your group membership did not resolve to a level. This is a configuration problem with ' +
      'the account, not a decision about you.',
  },
};

/**
 * 503 is six different dependencies, and the words matter: every one of them
 * means "could not", never "may not". `shutting-down` gets its own line because
 * it is the condition that strands an async job as `abandoned`.
 */
const UNAVAILABLE_COPY: Record<UnavailableCode, string> = {
  'oidc-provider-unavailable':
    'Okta could not be reached, so your access could not be verified. This is not a refusal — your access has not changed.',
  'directory-unavailable':
    'The directory service could not be reached, so your access could not be verified. This is not a refusal.',
  'member-directory-unavailable':
    'The member directory could not be reached, so account details could not be loaded. This is not a refusal.',
  'audit-storage-unavailable':
    'The audit store could not be reached. Audit records are not lost — they cannot be read right now.',
  'job-storage-unavailable':
    'The job store could not be reached, so job state could not be read. Any running job is unaffected.',
  'shutting-down':
    'The service is restarting. Anything you just submitted may or may not have been applied, and a job in flight will be marked abandoned rather than resumed. Re-check the current state before retrying.',
};

/**
 * Said when the 503 body carried no recognised code. It still has to carry the
 * one thing every 503 must say, because this is the branch most easily misread
 * as a refusal.
 */
const UNAVAILABLE_FALLBACK =
  'Something the API depends on could not be reached, so this could not be completed. This is not a refusal.';

/**
 * A 409 is a refusal, never a fault — but not the same refusal six times.
 * Two of these codes name a state that can be changed, two name a
 * precondition that lives on another screen, and two are facts about the
 * request itself that no state would ever permit. That is why one sentence
 * cannot cover all six.
 */
const CONFLICT_COPY: Record<ConflictCode, string> = {
  suspended:
    'This container is locked. Unlock it, then run this again.',
  'gateway-binds-attached':
    'This container still has gateway bindings attached. Remove them before deleting it.',
  'account-inactive':
    'The owning hosting account is inactive. Activate the account before binding a domain to its container.',
  'disk-shrink':
    'Disks cannot be shrunk. No container state allows this — the new size must be at least the current one.',
  'dangerous-memory-reduction':
    'The new memory allocation is below what the container is currently using. Acknowledge that on the form to proceed.',
  'quota-exceeded':
    'The new allocation would put the owner over their tier quota. Raise the tier, or choose a smaller size.',
};

const CONFLICT_FALLBACK = 'The target is in a state that refuses this action.';

export function ApiErrorNotice({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (!(error instanceof ApiError)) {
    return (
      <div role="alert">
        <h2>Something went wrong</h2>
        <p>{error instanceof Error ? error.message : 'Unknown error.'}</p>
      </div>
    );
  }

  const { lead, context } = detail(error);

  return (
    <div role="alert">
      <h2>{title(error)}</h2>
      <p>{lead}</p>
      {/* Context, never preamble — it sits below whatever the API itself said. */}
      {context && <p className="notice-context">{context}</p>}

      {error.isRetryable && onRetry && (
        <button type="button" onClick={onRetry}>
          Try again
        </button>
      )}

      {error.kind === 'unauthenticated' && <a href={loginUrl()}>Log in</a>}

      {/* How support traces what happened. Always show it when present. */}
      {error.opId && (
        <p>
          Reference <OpId opId={error.opId} />
        </p>
      )}
    </div>
  );
}

/**
 * What the API actually said, or nothing.
 *
 * Deliberately `body.message` rather than `error.message`: the latter falls
 * back to `statusText` when the body could not be parsed (see
 * `apiErrorFromResponse`), so a 502 HTML error page from Caddy would otherwise
 * lead with the word "Bad Gateway" as though the API had explained itself.
 * Only a structured message earns the lead.
 *
 * Typed as `unknown` on the way in, against the type that says `string`.
 * `apiErrorFromResponse` casts whatever JSON came back to `ApiErrorBody`
 * without checking it, and not everything that answers on this origin is the
 * API: a proxy or a WAF can send `message` as a number, an object or an array,
 * and `.trim()` on any of those throws. This is the surface every other failure
 * is reported through, including the ones where the API is gone — it is the one
 * component in the console that must not be able to fail.
 */
function serverMessage(error: ApiError): string | undefined {
  const message: unknown = error.body?.message;
  if (typeof message !== 'string') return undefined;

  const trimmed = message.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function title(error: ApiError): string {
  switch (error.kind) {
    case 'unauthenticated':
      return 'You are signed out';
    case 'forbidden':
      return knownForbiddenCopy(error)?.title ?? 'Refused';
    case 'not-found':
      return 'Not found';
    case 'rate-limited':
      return 'Too many requests';
    case 'upstream':
      // The heading asserts a cause too. "Not responding" is right for an
      // opaque 502, and wrong above an upstream that answered with a precise
      // reason — so it is only claimed when nothing else was said.
      return serverMessage(error)
        ? 'An upstream system reported a failure'
        : 'A platform service is not responding';
    case 'api-unreachable':
      // Names the one system that is actually down. "A platform service is not
      // responding" above is deliberately vaguer, because a 502 from the API
      // could be any of three.
      return 'The Platform Control API is not answering';
    case 'unavailable':
      // NOT "access denied". Nobody could check.
      return error.isShutdown ? 'The service is restarting' : 'Temporarily unavailable';
    case 'invalid-request':
      return 'That request was not valid';
    case 'conflict':
      // Only ever true of `suspended`; the other five codes need a different fix.
      return 'That action was refused';
    case 'server':
      return 'The API returned an error';
    case 'network':
      return 'Could not reach the API';
  }
}

/**
 * `lead` is the first thing read and is the API's own message wherever it sent
 * one; `context` is this file's generic copy, kept because it carries the
 * taxonomy distinctions — which dependency, which of the three 403s, that a 503
 * is not a refusal — that the server's one-line message does not.
 */
interface Detail {
  lead: string;
  context?: string;
}

function detail(error: ApiError): Detail {
  const server = serverMessage(error);

  switch (error.kind) {
    case 'unauthenticated':
      return {
        lead: server ?? 'Your session expired or was ended.',
        context: 'Log in to continue.',
      };

    case 'forbidden': {
      const known = knownForbiddenCopy(error);
      // The per-code copy stays visible either way: it is what keeps
      // "not an operator", "level too low" and "level unreadable" three
      // different answers rather than one shrug.
      return {
        lead: server ?? known?.detail ?? 'The API refused this action.',
        ...(server && known ? { context: known.detail } : {}),
      };
    }

    case 'not-found':
      return { lead: server ?? 'The API has no record of what this screen asked for.' };

    case 'rate-limited': {
      // The wait is our own knowledge, from the Retry-After header rather than
      // the message body, so it belongs after whatever the API said.
      const wait =
        error.retryAfterSeconds !== undefined
          ? `Try again in ${error.retryAfterSeconds} seconds.`
          : 'Wait a moment and try again.';
      return { lead: server ?? 'You have hit the request limit.', context: wait };
    }

    case 'upstream':
      // 502. A message here is the upstream explaining itself — a locked VMID,
      // a rejected config — and is usually the most actionable text on the
      // screen. Where it exists, all this file adds is where it came from; the
      // old "this is not something you did wrong" preamble stays only for the
      // genuinely opaque case, which is the only case it was ever true of.
      return server
        ? {
            lead: server,
            context:
              'This came back from an upstream system — Proxmox, pfSense or the gateway — rather than from Platform Control itself.',
          }
        : {
            lead: 'An upstream system (Proxmox, pfSense or the gateway) failed. This is not something you did wrong.',
          };

    case 'api-unreachable':
      /*
       * A 502 or 504 that the API did not write. The reverse proxy in front of
       * it answered instead, which it only does when the API process is not
       * there to answer for itself.
       *
       * `server` is not consulted even where a body exists, and this is the one
       * branch where that is deliberate: whatever text came back was written by
       * something other than the API — the proxy, a WAF, an error page — and
       * letting it lead would present it as the API explaining itself, which is
       * the exact confusion this kind exists to end.
       */
      return {
        lead: 'Platform Control itself could not be reached. The reverse proxy answered this request because the API behind it did not — it is stopped, restarting, or has crashed.',
        context:
          'This is not a failure of Proxmox, pfSense or the gateway — the request never got far enough to touch them. A restart takes seconds, so it is worth trying again.',
      };

    case 'unavailable': {
      // 503. The most important branch in this file: never a refusal. The
      // dependency-specific copy is kept whichever way round it renders,
      // because it is the part that says so.
      const code = error.unavailableCode;
      const copy = (code && UNAVAILABLE_COPY[code]) || UNAVAILABLE_FALLBACK;
      return server ? { lead: server, context: copy } : { lead: copy };
    }

    case 'invalid-request':
      // A 400 from our own UI is a bug in the UI, not something the operator
      // did. The API says which field; we say what to do about it.
      return {
        lead: server ?? 'The API rejected the request as malformed.',
        context:
          'If you did not type this by hand, it is a bug — please report it with the reference below.',
      };

    case 'conflict': {
      // 409. Never a fault — the API is declining, not failing. The per-code
      // sentence is kept whichever way round it renders, because it is the
      // part that says what to do instead.
      const code = error.conflictCode;
      const copy = (code && CONFLICT_COPY[code]) || CONFLICT_FALLBACK;
      return server ? { lead: server, context: copy } : { lead: copy };
    }

    case 'server':
      return { lead: server ?? 'The API returned an error without explaining it.' };

    case 'network':
      // No body and no server at all — `message` here is whatever fetch
      // rejected with, which names no cause an operator can act on.
      return { lead: 'The request never reached the API. Check that you are connected to the VPN.' };
  }
}

/** The copy for a 403 whose code we recognise, or nothing when we do not. */
function knownForbiddenCopy(error: ApiError): { title: string; detail: string } | undefined {
  const code = error.forbiddenCode;
  return code && code in FORBIDDEN_COPY ? FORBIDDEN_COPY[code] : undefined;
}
